import React from "react"
import { Link, useNavigate } from "react-router-dom"
import CustomButton from "../../components/OnboardingComponents/CustomButton"
import sendIcon from "../../assets/images/city-hub/send.svg"
import amokEarthWordmark from "../../assets/images/public/Amok.Earth_.svg"
import {
  PUBLIC_FOOTER_LINK_COLUMNS,
  PUBLIC_SITE_HOME,
  PUBLIC_SOCIAL_LINKS,
  REQUEST_INVITE_PATH,
} from "./publicCityPaths"

const ArrowIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M2 6H10"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7 3L10 6L7 9"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const SocialIcon = ({ name }) => {
  switch (name) {
    case "facebook":
      return (
        <svg
          viewBox="0 0 512 512"
          width="24"
          height="24"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            fill="currentColor"
            d="M504 256C504 119 393 8 256 8S8 119 8 256c0 123.78 90.69 226.38 209.25 245V327.69h-63V256h63v-54.64c0-62.15 37-96.48 93.67-96.48 27.14 0 55.52 4.84 55.52 4.84v61h-31.28c-30.8 0-40.41 19.12-40.41 38.73V256h68.78l-11 71.69h-57.78V501C413.31 482.38 504 379.78 504 256z"
          />
        </svg>
      )
    case "bluesky":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
        >
          <path
            fill="currentColor"
            d="M5.46821 3.94237C7.30257 5.31093 9.27564 8.08578 10 9.57493C10.7245 8.08589 12.6975 5.3109 14.5319 3.94237C15.8555 2.95488 18 2.19081 18 4.62211C18 5.10768 17.7199 8.7011 17.5556 9.28448C16.9844 11.3127 14.9033 11.83 13.0521 11.5169C16.288 12.0642 17.1111 13.8771 15.3334 15.6899C11.9572 19.1329 10.4808 14.8261 10.1023 13.7225C10.0329 13.5202 10.0005 13.4256 10 13.506C9.99952 13.4256 9.96707 13.5202 9.89772 13.7225C9.5194 14.8261 8.04303 19.133 4.66658 15.6899C2.88882 13.8771 3.71196 12.0641 6.94786 11.5169C5.09662 11.83 3.01547 11.3127 2.44444 9.28448C2.28013 8.70105 2 5.10762 2 4.62211C2 2.19081 4.14459 2.95488 5.46812 3.94237H5.46821Z"
          />
        </svg>
      )
    case "youtube":
      return (
        <svg
          viewBox="0 0 576 512"
          width="24"
          height="24"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            fill="currentColor"
            d="M549.655 124.083c-6.281-23.65-24.787-42.276-48.284-48.597C458.781 64 288 64 288 64S117.22 64 74.629 75.486c-23.497 6.322-42.003 24.947-48.284 48.597-11.412 42.867-11.412 132.305-11.412 132.305s0 89.438 11.412 132.305c6.281 23.65 24.787 41.5 48.284 47.821C117.22 448 288 448 288 448s170.78 0 213.371-11.486c23.497-6.321 42.003-24.171 48.284-47.821 11.412-42.867 11.412-132.305 11.412-132.305s0-89.438-11.412-132.305zm-317.51 213.508V175.185l142.739 81.205-142.739 81.201z"
          />
        </svg>
      )
    default:
      return null
  }
}

const FooterLink = ({ item }) => {
  const content = (
    <>
      <ArrowIcon />
      <span>{item.label}</span>
    </>
  )

  if (item.isApp) {
    return (
      <Link to={item.href} className="public-site-footer__link">
        {content}
      </Link>
    )
  }

  return (
    <a href={item.href} className="public-site-footer__link">
      {content}
    </a>
  )
}

/**
 * Dark marketing footer matching the public city mock + live CTA band.
 */
const PublicSiteFooter = () => {
  const navigate = useNavigate()

  return (
    <footer className="public-site-footer">
      <div className="public-site-footer__shell">
        <div className="public-site-footer__cta">
          <h2 className="public-site-footer__cta-title">
            Ready to build something real?
          </h2>
          <p className="public-site-footer__cta-text">
            amok.earth grows one invite at a time, from artists who already
            trust the person they&apos;re inviting. Ask a member for an invite,
            or apply below and a City Editor in your area will follow up.
          </p>
          <CustomButton
            variant="secondary"
            size="md"
            trailingIconSrc={sendIcon}
            trailingIconAlt=""
            className="public-site-footer__cta-btn"
            onClick={() => navigate(REQUEST_INVITE_PATH)}
          >
            Request an Invite
          </CustomButton>
        </div>

        <div className="public-site-footer__panel">
          <a
            href={PUBLIC_SITE_HOME}
            className="public-site-footer__brand"
            aria-label="Amok Earth home"
          >
            <img
              src={amokEarthWordmark}
              alt="AMOK.EARTH"
              className="public-site-footer__brand-img"
            />
          </a>

          <p className="public-site-footer__tagline">
            Every project starts somewhere.
          </p>

          <div className="public-site-footer__columns">
            {PUBLIC_FOOTER_LINK_COLUMNS.map((column, columnIndex) => (
              <ul
                key={`footer-col-${columnIndex}`}
                className="public-site-footer__col"
              >
                {column.map(item => (
                  <li key={item.label}>
                    <FooterLink item={item} />
                  </li>
                ))}
              </ul>
            ))}
          </div>

          <ul className="public-site-footer__social">
            {PUBLIC_SOCIAL_LINKS.map(item => (
              <li key={item.label}>
                <a
                  href={item.href}
                  className="public-site-footer__social-btn"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={item.label}
                >
                  <SocialIcon name={item.icon} />
                </a>
              </li>
            ))}
          </ul>
        </div>

        <p className="public-site-footer__copy">
          © Amok {new Date().getFullYear()}. All Rights Reserved.
        </p>
      </div>
    </footer>
  )
}

export default PublicSiteFooter
